<template>
  <v-container fluid>
    <v-row>
      <!-- Upcoming Invoice Preview -->
      <v-col cols="12">
        <v-card>
          <v-card-title class="body-2 v-card__subtitle">
            <v-icon left small class="mr-2">mdi-invoice-text-clock</v-icon>
            Rechnungsvorschau
          </v-card-title>
          <v-card-text>
            <!-- Subscription Selection -->
            <v-row dense class="mb-4">
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedSubscriptionForPreview"
                  :items="activeSubscriptions"
                  item-text="displayName"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  label="Abonnement auswählen"
                  prepend-inner-icon="mdi-playlist-check"
                  :disabled="activeSubscriptions.length === 0"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>
                        {{ item.displayName }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="caption">
                        Status: {{ item.status }} | Nächste Abrechnung:
                        {{
                          item.currentPeriodEnd
                            ? timestampConverter(item.currentPeriodEnd)
                            : "N/A"
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.displayName }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" md="6" class="d-flex align-center">
                <v-btn
                  color="primary"
                  outlined
                  :disabled="!selectedSubscriptionForPreview"
                  @click="fetchInvoicePreview"
                  :loading="loadingPreview"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Vorschau laden
                </v-btn>
              </v-col>
            </v-row>

            <!-- Preview Data Display -->
            <v-row dense v-if="selectedSubscriptionForPreview">
              <v-col cols="12" sm="6" md="3">
                <div class="caption grey--text text--darken-1">
                  Voraussichtliches Datum
                </div>
                <div class="body-1 font-weight-medium">
                  <span v-if="currentBillingPeriod">
                    {{ timestampConverter(currentBillingPeriod.end) }}
                  </span>
                  <span v-else class="grey--text">Nicht verfügbar</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="caption grey--text text--darken-1">
                  Geschätzter Betrag
                </div>
                <div class="body-1 font-weight-medium info--text">
                  {{ estimatedCurrentPeriodCost }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="caption grey--text text--darken-1">
                  Aktive Lizenzen
                </div>
                <div class="body-1 font-weight-medium">
                  {{ totalActiveLicenses }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="caption grey--text text--darken-1">
                  Ausstehende Änderungen
                </div>
                <div class="body-1 font-weight-medium">
                  <v-chip
                    v-if="upcomingChanges.length > 0"
                    small
                    color="warning"
                    outlined
                  >
                    {{ upcomingChanges.length }} Änderungen
                  </v-chip>
                  <span v-else class="grey--text">Keine</span>
                </div>
              </v-col>
            </v-row>

            <!-- Empty State -->
            <v-row dense v-else>
              <v-col cols="12" class="text-center py-8">
                <v-icon size="64" color="grey lighten-1"
                  >mdi-file-document-outline</v-icon
                >
                <div class="body-1 grey--text mt-2">
                  Wählen Sie eine Subscription aus, um eine Vorschau zu laden
                </div>
              </v-col>
            </v-row>

            <v-divider
              class="my-3"
              v-if="selectedSubscriptionForPreview"
            ></v-divider>
            <div
              class="caption grey--text"
              v-if="selectedSubscriptionForPreview"
            >
              <v-icon x-small left>mdi-information</v-icon>
              Diese Vorschau basiert auf aktuellen aktiven Lizenzen und
              ausstehenden Änderungen. Der tatsächliche Betrag kann abweichen.
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Subscriptions Table -->
      <v-col cols="12">
        <v-card>
          <v-data-table
            :items="subscriptions"
            :headers="subscriptionsHeaders"
            sort-by="startDate"
            item-key="id"
            :mobile-breakpoint="0"
            :items-per-page="5"
            disable-sort
          >
            <template v-slot:top>
              <slot name="top">
                <v-card flat>
                  <v-card-title class="body-2 v-card__subtitle">
                    <v-icon left small class="mr-2">mdi-playlist-check</v-icon
                    >Abonnements
                  </v-card-title>
                </v-card>
              </slot>
            </template>
            <template v-slot:[`item.startDate`]="{ item }">
              <div
                v-if="item.nickname || item.plan.nickname"
                class="font-weight-medium mt-2"
              >
                {{ item.nickname ? item.nickname : item.plan.nickname }}
              </div>
              <div style="line-height: 1.2" class="mb-2">
                <div v-if="item.currentPeriodStart && item.currentPeriodEnd">
                  <small
                    ><strong>Aktueller Abrechnungszeitraum: </strong>
                    {{ timestampConverter(item.currentPeriodStart) }} bis
                    {{ timestampConverter(item.currentPeriodEnd) }}</small
                  >
                </div>
                <div>
                  <small
                    ><strong>Startzeitpunkt: </strong>
                    {{ timestampConverter(item.startDate) }}</small
                  >
                  <small v-if="item.endedAt"
                    >, <strong>Endzeitpunkt: </strong>
                    {{ timestampConverter(item.endedAt) }}</small
                  >
                </div>
                <div v-if="checkIfSupport" class="mt-1">
                  <code>{{ item.id }}</code>
                </div>
              </div>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <subscription-status-chip :status="item.status" small>
              </subscription-status-chip>
            </template>
            <template v-if="hasWriteAccess" v-slot:[`item.actions`]="{}">
              <v-btn
                color="error"
                depressed
                outlined
                text
                small
                @click="$emit('open-portal')"
                ><v-icon left small>mdi-cancel</v-icon>Kündigen</v-btn
              >
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SubscriptionStatusChip from "@/components/admin/billing/SubscriptionStatusChip.vue";
import { billingHelpersMixin } from "@/mixins/billingHelpers";

export default {
  name: "BillingTabSubscriptions",
  mixins: [billingHelpersMixin],
  components: {
    SubscriptionStatusChip,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
    subscriptions: {
      type: Array,
      default: () => [],
    },
    licenses: {
      type: Array,
      default: () => [],
    },
    hasWriteAccess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedSubscriptionForPreview: null,
      loadingPreview: false,
    };
  },
  computed: {
    subscriptionsHeaders() {
      const headers = [
        { text: "Abonnehmentdetails", value: "startDate" },
        { text: "Status", value: "status" },
      ];
      if (this.hasWriteAccess) {
        headers.push({
          text: "",
          value: "actions",
          sortable: false,
          align: "right",
        });
      }
      return headers;
    },
    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    activeSubscriptions() {
      return (
        this.subscriptions
          // .filter((sub) => sub.status === "active" || sub.status === "trialing")
          .map((sub) => ({
            ...sub,
            displayName: `${sub.nickname || sub.plan?.nickname || sub.id} (${
              sub.status
            })`,
          }))
      );
    },
    currentBillingPeriod() {
      if (!this.selectedSubscriptionForPreview) return null;

      const selectedSubscription = this.subscriptions.find(
        (sub) => sub.id === this.selectedSubscriptionForPreview,
      );

      if (
        selectedSubscription &&
        selectedSubscription.currentPeriodStart &&
        selectedSubscription.currentPeriodEnd
      ) {
        return {
          start: selectedSubscription.currentPeriodStart,
          end: selectedSubscription.currentPeriodEnd,
        };
      }
      return null;
    },
    estimatedCurrentPeriodCost() {
      const total = this.licenses
        .filter((license) => license.status === "active")
        .reduce((sum, license) => {
          return sum + (license.unitAmount || 0);
        }, 0);

      if (total > 0 && this.licenses.length > 0) {
        const currency = this.licenses[0].currency || "EUR";
        return this.getAmountFromCents(total, currency);
      }
      return "0,00 EUR";
    },
    totalActiveLicenses() {
      return this.licenses.filter((license) => license.status === "active")
        .length;
    },
    upcomingChanges() {
      const changes = [];

      this.licenses
        .filter((license) => license.status === "pendingCancellation")
        .forEach((license) => {
          changes.push({
            id: license.licenseId,
            type: "cancellation",
            description: `Kündigung von ${
              license.license?.title || "Lizenz"
            } für ${license.user?.firstName} ${license.user?.lastName}`,
            effectiveDate: license.periodEnd,
          });
        });

      changes.sort((a, b) => {
        if (!a.effectiveDate || !b.effectiveDate) return 0;
        return a.effectiveDate.toDate() - b.effectiveDate.toDate();
      });

      return changes;
    },
  },
  mounted() {
    // Auto-select first active subscription
    if (this.subscriptions.length > 0 && !this.selectedSubscriptionForPreview) {
      const firstActive = this.subscriptions.find(
        (sub) => sub.status === "active" || sub.status === "trialing",
      );
      if (firstActive) {
        this.selectedSubscriptionForPreview = firstActive.id;
      }
    }
  },
  methods: {
    async fetchInvoicePreview() {
      if (!this.selectedSubscriptionForPreview) return;

      this.loadingPreview = true;
      try {
        // TODO: Implement Cloud Function call to fetch invoice preview from Stripe
        // const result = await this.$store.dispatch('billing/fetchInvoicePreview', {
        //   customerId: this.customer.id,
        //   subscriptionId: this.selectedSubscriptionForPreview
        // });

        console.log(
          "Fetching invoice preview for subscription:",
          this.selectedSubscriptionForPreview,
        );

        this.$store.commit("snackbar/setSnackbar", {
          type: "info",
          message: "Rechnungsvorschau-Funktion wird implementiert",
        });
      } catch (error) {
        console.error("Error fetching invoice preview:", error);
        this.$store.commit("snackbar/setSnackbar", {
          type: "error",
          message: "Fehler beim Laden der Rechnungsvorschau",
        });
      } finally {
        this.loadingPreview = false;
      }
    },
  },
};
</script>
